import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import AdminService from '../../../services/adminService';
import { useAuth } from '../../../context/AuthContext';
import { LoadingSpinner, PageHeader, StatusBadge, ActionButton } from '../../../components/ui';

const CasesListPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [status, setStatus] = useState('all');
  const [assigned, setAssigned] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [savedViews, setSavedViews] = useState(() => {
    try {
      const raw = localStorage.getItem('cases_saved_views');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [activeView, setActiveView] = useState('');
  const [myCasesOnly, setMyCasesOnly] = useState(() => {
    try {
      const val = localStorage.getItem('cases_my_only');
      return val ? JSON.parse(val) : false;
    } catch { return false; }
  });
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [toast, setToast] = useState(null);
  const [tasksCountMap, setTasksCountMap] = useState({});

  const fetchCases = async () => {
    try {
      setLoading(true);
      setError(null);
      const effectiveAssigned = myCasesOnly ? (user?.email || user?.id || assigned) : assigned;
      const { rows: data, count } = await AdminService.getCasesPaged({ page, pageSize, status, assignedTo: effectiveAssigned, dateFrom, dateTo, search, sortBy, sortDir });
      setRows(Array.isArray(data) ? data : []);
      setTotalCount(count || 0);
    } catch (e) {
      console.error('Cases load error', e);
      setError('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCases(); }, []);
  useEffect(() => { fetchCases(); }, [status, assigned, dateFrom, dateTo, search, sortBy, sortDir, page, pageSize, myCasesOnly, user?.email]);
  // Load open tasks count for listed cases
  useEffect(() => {
    (async () => {
      try {
        const ids = Array.from(new Set((rows || []).map(r => r.id)));
        if (!ids.length) { setTasksCountMap({}); return; }
        const { data, error } = await supabase
          .from('case_tasks')
          .select('id, case_id, status')
          .in('case_id', ids);
        if (error) throw error;
        const map = {};
        (data || []).forEach(t => {
          if (t.status === 'done') return;
          map[t.case_id] = (map[t.case_id] || 0) + 1;
        });
        setTasksCountMap(map);
      } catch (e) {
        setTasksCountMap({});
      }
    })();
  }, [rows]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);
  // Persist My Cases toggle
  useEffect(() => {
    try { localStorage.setItem('cases_my_only', JSON.stringify(!!myCasesOnly)); } catch { }
  }, [myCasesOnly]);

  // Seed a default saved view: My In Progress
  useEffect(() => {
    try {
      if (user?.email && Object.keys(savedViews || {}).length === 0) {
        const defaultView = {
          search: '',
          status: 'in_progress',
          assigned: user.email,
          dateFrom: '',
          dateTo: '',
          sortBy: 'updated_at',
          sortDir: 'desc'
        };
        const next = { ...savedViews, 'My In Progress': defaultView };
        setSavedViews(next);
        localStorage.setItem('cases_saved_views', JSON.stringify(next));
      }
    } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const toggleSelect = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));
  };
  const selectAll = (checked) => {
    setSelectedIds(checked ? rows.map((r) => r.id) : []);
  };

  const applySavedView = (viewKey) => {
    const v = savedViews[viewKey];
    if (!v) return;
    setActiveView(viewKey);
    setSearch(v.search || '');
    setStatus(v.status || 'all');
    setAssigned(v.assigned || '');
    setDateFrom(v.dateFrom || '');
    setDateTo(v.dateTo || '');
    setSortBy(v.sortBy || 'created_at');
    setSortDir(v.sortDir || 'desc');
    setPage(1);
  };
  const saveCurrentView = () => {
    const key = prompt('Save view as (name):');
    if (!key) return;
    const next = {
      ...savedViews,
      [key]: { search, status, assigned, dateFrom, dateTo, sortBy, sortDir }
    };
    setSavedViews(next);
    try { localStorage.setItem('cases_saved_views', JSON.stringify(next)); } catch { }
    setActiveView(key);
  };
  const deleteSavedView = (k) => {
    const next = { ...savedViews };
    delete next[k];
    setSavedViews(next);
    try { localStorage.setItem('cases_saved_views', JSON.stringify(next)); } catch { }
    if (activeView === k) setActiveView('');
  };

  const bulkSetStatus = async (newStatus) => {
    if (!selectedIds.length) return;
    try {
      setBulkBusy(true);
      await AdminService.bulkUpdateCases(selectedIds, { status: newStatus });
      await fetchCases();
      setSelectedIds([]);
      setToast({ type: 'success', message: `Updated ${selectedIds.length} case(s) to ${newStatus}` });
    } catch (e) {
      setToast({ type: 'error', message: 'Failed to update cases' });
    } finally { setBulkBusy(false); }
  };
  const bulkReassign = async () => {
    const who = prompt('Assign to (email/user id):');
    if (!who) return;
    try {
      setBulkBusy(true);
      await AdminService.bulkUpdateCases(selectedIds, { assigned_to: who });
      await fetchCases();
      setSelectedIds([]);
    } catch (e) { setToast({ type: 'error', message: 'Failed to reassign' }); }
    finally { setBulkBusy(false); }
  };
  const bulkDueDate = async () => {
    const date = prompt('Set due date (YYYY-MM-DD):');
    if (!date) return;
    try {
      setBulkBusy(true);
      await AdminService.bulkUpdateCases(selectedIds, { due_date: date });
      await fetchCases();
      setSelectedIds([]);
    } catch (e) { setToast({ type: 'error', message: 'Failed to update due date' }); }
    finally { setBulkBusy(false); }
  };

  const totalPages = Math.max(1, Math.ceil((totalCount || rows.length) / pageSize));
  const changeSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('desc'); }
  };

  // Load client names for display
  const [clientMap, setClientMap] = useState({});
  useEffect(() => {
    const loadClients = async () => {
      try {
        const ids = Array.from(new Set((rows || []).map(r => r.client_id).filter(Boolean)));
        if (!ids.length) { setClientMap({}); return; }
        const { data, error } = await supabase
          .from('clients')
          .select('id, first_name, last_name, primary_email')
          .in('id', ids);
        if (error) throw error;
        const map = {};
        (data || []).forEach(c => { map[c.id] = c; });
        setClientMap(map);
      } catch (e) {
        setClientMap({});
      }
    };
    loadClients();
  }, [rows]);

  const clientLabel = (id) => {
    const c = id ? clientMap[id] : null;
    if (!c) return '-';
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    return name || c.primary_email || c.id;
  };

  const isOverdue = (row) => {
    if (!row?.due_date) return false;
    if (String(row.status) === 'completed') return false;
    const today = new Date();
    const due = new Date(row.due_date);
    due.setHours(23, 59, 59, 999);
    return due < today;
  };

  const inlineUpdate = async (row, updates) => {
    try {
      await AdminService.updateCase(row.id, updates);
      await fetchCases();
      setToast({ type: 'success', message: 'Case updated' });
    } catch (e) {
      setToast({ type: 'error', message: 'Failed to update case' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
        <LoadingSpinner size="large" message="Loading cases..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Header */}
      <PageHeader
        title="🗂️ Case Management"
        subtitle="Track active and completed cases with comprehensive status management"
      />

      {/* Filters & Saved Views */}
      <div className="bg-gray-800 rounded-lg shadow border border-gray-700 mb-6">
        <div className="p-4">
          {toast && (
            <div className={`mb-3 p-2 rounded border ${toast.type === 'success' ? 'bg-green-900/20 border-green-800 text-green-400' : 'bg-red-900/20 border-red-800 text-red-400'}`}>
              {toast.message}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mb-4">
            <ActionButton onClick={saveCurrentView} variant="secondary" icon="💾">Save View</ActionButton>
            {Object.keys(savedViews).length > 0 && (
              <>
                <select className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-sm text-white focus:ring-2 focus:ring-blue-500" value={activeView} onChange={(e) => applySavedView(e.target.value)}>
                  <option value="">Select view…</option>
                  {Object.keys(savedViews).map((k) => (<option key={k} value={k}>{k}</option>))}
                </select>
                {activeView && (
                  <button className="px-3 py-1.5 rounded text-sm font-medium bg-red-900/20 text-red-400 border border-red-800 hover:bg-red-900/40" onClick={() => deleteSavedView(activeView)}>
                    🗑️ Delete View
                  </button>
                )}
              </>
            )}
            <button
              className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${myCasesOnly ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-700 text-white border-gray-600 hover:bg-gray-600'}`}
              onClick={() => { setMyCasesOnly((v) => !v); setPage(1); }}
              title={user?.email ? `Filter assigned to ${user.email}` : 'Filter my cases'}
            >
              {myCasesOnly ? '✔︎ My Cases' : 'My Cases'}
            </button>
            <button
              className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${overdueOnly ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-700 text-white border-gray-600 hover:bg-gray-600'}`}
              onClick={() => setOverdueOnly((v) => !v)}
              title="Show only overdue cases"
            >
              {overdueOnly ? '✔︎ Overdue only' : 'Overdue only'}
            </button>
            <ActionButton
              variant="secondary"
              onClick={() => {
                const today = new Date();
                const to = new Date();
                to.setDate(today.getDate() + 7);
                const iso = (d) => d.toISOString().slice(0, 10);
                setDateFrom(iso(today));
                setDateTo(iso(to));
                setPage(1);
              }}
              title="Cases due within the next 7 days"
            >
              Due this week
            </ActionButton>
            <button
              className="px-3 py-1.5 rounded text-sm font-medium bg-red-900/20 text-red-400 border border-red-800 hover:bg-red-900/40"
              onClick={() => {
                setSearch(''); setStatus('all'); setAssigned(''); setDateFrom(''); setDateTo(''); setSortBy('created_at'); setSortDir('desc'); setMyCasesOnly(false); setOverdueOnly(false); setActiveView(''); setSelectedIds([]); setPage(1);
                setToast({ type: 'success', message: 'Filters cleared' });
              }}
              title="Clear all filters"
            >
              Clear filters
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="🔍 Search title, stage, assignee (Enter to search)" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
            <input type="date" className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
            <input type="date" className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="👤 Assigned to" value={assigned} onChange={(e) => { setAssigned(e.target.value); setPage(1); }} />
            <select className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="all">All statuses</option>
              {['open', 'in_progress', 'on_hold', 'completed'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value) || 20); setPage(1); }} className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800 text-red-400 p-4 rounded mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* Table + Bulk Actions */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow mb-6">
        <div className="flex items-center justify-between p-3 bg-gray-900/50 border-b border-gray-700">
          <label className="text-xs text-gray-400 flex items-center gap-2">
            <input type="checkbox" checked={selectedIds.length > 0 && selectedIds.length === rows.length} onChange={(e) => selectAll(e.target.checked)} className="rounded border-gray-600 bg-gray-700 text-blue-500" />
            Select all
          </label>
          <div className="flex flex-wrap gap-2">
            <button disabled={!selectedIds.length || bulkBusy} onClick={() => bulkSetStatus('in_progress')} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600 disabled:opacity-50">Mark In Progress</button>
            <button disabled={!selectedIds.length || bulkBusy} onClick={() => bulkSetStatus('completed')} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600 disabled:opacity-50">Mark Completed</button>
            <button disabled={!selectedIds.length || bulkBusy} onClick={bulkReassign} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600 disabled:opacity-50">Reassign…</button>
            <button disabled={!selectedIds.length || bulkBusy} onClick={bulkDueDate} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600 disabled:opacity-50">Set Due…</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">
                  <input type="checkbox" checked={selectedIds.length > 0 && selectedIds.length === rows.length} onChange={(e) => selectAll(e.target.checked)} className="rounded border-gray-600 bg-gray-700 text-blue-500" />
                </th>
                <th className="cursor-pointer bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase" onClick={() => changeSort('title')}>Title</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Client</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Open Tasks</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Stage</th>
                <th className="cursor-pointer bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase" onClick={() => changeSort('status')}>Status</th>
                <th className="cursor-pointer bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase" onClick={() => changeSort('priority')}>Priority</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Assignee</th>
                <th className="cursor-pointer bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase" onClick={() => changeSort('due_date')}>Due</th>
                <th className="cursor-pointer bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase" onClick={() => changeSort('updated_at')}>Updated</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(overdueOnly ? rows.filter((c) => isOverdue(c)) : rows).map((c) => (
                <tr key={c.id} className="hover:bg-gray-700/50 transition-colors">
                  <td className="p-3 border-b border-gray-700">
                    <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={(e) => toggleSelect(c.id, e.target.checked)} className="rounded border-gray-600 bg-gray-700 text-blue-500" />
                  </td>
                  <td className="p-3 border-b border-gray-700 font-semibold text-white">{c.title || c.id}</td>
                  <td className="p-3 border-b border-gray-700">
                    {c.client_id ? (
                      <Link to={`/admin/clients/${c.client_id}`} className="text-blue-400 hover:text-blue-300">
                        {clientLabel(c.client_id)}
                      </Link>
                    ) : <span className="text-gray-500">-</span>}
                  </td>
                  <td className="p-3 border-b border-gray-700 text-gray-400">{tasksCountMap[c.id] || 0}</td>
                  <td className="p-3 border-b border-gray-700 text-gray-400">{c.current_stage || '-'}</td>
                  <td className="p-3 border-b border-gray-700">
                    <select
                      className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                      value={c.status || 'open'}
                      onChange={(e) => inlineUpdate(c, { status: e.target.value })}
                    >
                      {['open', 'in_progress', 'on_hold', 'completed'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-3 border-b border-gray-700">
                    <StatusBadge status={c.priority}>
                      {c.priority || '-'}
                    </StatusBadge>
                  </td>
                  <td className="p-3 border-b border-gray-700">
                    <input
                      className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                      placeholder="email or user id"
                      defaultValue={c.assigned_to || ''}
                      onBlur={(e) => e.target.value !== (c.assigned_to || '') && inlineUpdate(c, { assigned_to: e.target.value })}
                    />
                  </td>
                  <td className="p-3 border-b border-gray-700">
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500"
                        defaultValue={c.due_date || ''}
                        onBlur={(e) => e.target.value !== (c.due_date || '') && inlineUpdate(c, { due_date: e.target.value })}
                      />
                      {isOverdue(c) && <span className="px-2 py-1 rounded text-xs font-medium border bg-red-900/20 text-red-400 border-red-800">Overdue</span>}
                    </div>
                  </td>
                  <td className="p-3 border-b border-gray-700 text-gray-400 text-sm">{c.updated_at ? new Date(c.updated_at).toLocaleString() : '-'}</td>
                  <td className="p-3 border-b border-gray-700 text-right">
                    <div className="flex gap-2 justify-end">
                      <Link to={`/admin/cases/${c.id}`} className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Open</Link>
                      {c.submission_id && <Link to={`/admin/submissions/${c.submission_id}`} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-700 text-white border border-gray-600 hover:bg-gray-600">Lead</Link>}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-gray-500">No cases found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg p-4">
        <div className="text-sm text-gray-400">Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount}</div>
        <div className="flex gap-2">
          <ActionButton disabled={page <= 1} onClick={() => setPage(1)} variant="secondary">First</ActionButton>
          <ActionButton disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} variant="secondary">Prev</ActionButton>
          <span className="px-3 py-1.5 text-sm font-medium text-white">Page {page} / {totalPages}</span>
          <ActionButton disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} variant="secondary">Next</ActionButton>
          <ActionButton disabled={page >= totalPages} onClick={() => setPage(totalPages)} variant="secondary">Last</ActionButton>
        </div>
      </div>
    </div>
  );
};

export default CasesListPage;
